import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
    connectedCallback() {
    if (this.#isButtonMode) {
      this.setAttribute('infinite-scroll', 'false');
    }

    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
        this.setAttribute('initialized', '');
        
    if (this.#isButtonMode) {
      this.#setupShowMoreButton();
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }
    
    get #isButtonMode() {
    return this.getAttribute('load-mode') === 'button';
  }

  #setupShowMoreButton() {
    const button = this.refs?.showMoreButton;
    if (!(button instanceof HTMLButtonElement)) return;

    button.addEventListener('click', this.#handleShowMore);
  }

  #handleShowMore = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const nextUrl = button.dataset.nextUrl;
    const sectionId = button.dataset.sectionId;

    if (!nextUrl || !sectionId) return;

    // Visual feedback
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // Build the section-rendering URL so Shopify returns only this section's HTML
      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Grab the new product <li> items from the fetched page
      const newCards = /** @type {NodeListOf<HTMLElement>} */ (
        parsed.querySelectorAll('li[data-product-id]')
      );

      // Find the current card list in our DOM
      const cardList = this.querySelector('.product-grid');
      if (!cardList) throw new Error('Card list container not found');

      // Append new cards with a subtle fade-in
      for (const card of newCards) {
        card.style.opacity = '0';
        card.style.transition = 'opacity 0.3s ease';
        cardList.appendChild(card);
        // Trigger reflow then fade in
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            card.style.opacity = '1';
          });
        });
      }

      // Update history so back-navigation returns to the right page
      const currentUrl = new URL(window.location.href);
      const newPage = fetchUrl.searchParams.get('page');
      if (newPage) {
        currentUrl.searchParams.set('page', newPage);
        history.replaceState({}, '', currentUrl.toString());
      }

      // Find the *next* page URL in the fetched HTML and update the button,
      // or remove the button entirely if there are no more pages.
      const nextButton = parsed.querySelector('[ref="showMoreContainer"] [ref="showMoreButton"]');
      const container = this.refs?.showMoreContainer;

      if (nextButton instanceof HTMLButtonElement && container) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl ?? '';
        button.disabled = false;
        button.removeAttribute('aria-busy');
      } else {
        // No more pages — hide the button container
        container?.remove();
      }
    } catch (error) {
      console.error('[ResultsList] Show more failed:', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
